import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect} from "react";
import {useParams} from "react-router";

import {GuideStateApi} from "@khan/guide-state";

import ErrorLoadingPage from "#app-shell-package/errors/error-loading-page.tsx";
import NotFound from "#app-shell-package/errors/not-found.tsx";
import PageRoot from "#app-shell-package/page-root.tsx";
import DefaultWithKhanmigoPageTemplate from "#app-shell-package/page-templates/with-khanmigo-templates/default-with-khanmigo-page-template.tsx";
import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import useProfile from "#data-access/use-profile.ts";
import {useUpdatePageSettings} from "#data-access/use-update-page-settings.ts";

import {withStrings} from "./locales/existing-user-program-page/load-strings.tsx";
import ScratchpadPage from "./scratchpad-page.tsx";
import {useProgram} from "./use-program.ts";

/**
 * This wrapper function waits until the user profile is loaded before rendering
 * the page.  This is not ideal from an UX point of view because all the user sees
 * is a spinner while we could be showing them the scratchpad while we wait to load
 * the profile.  It would be nice to fix this eventually, but due to the convoluted
 * nature of the code solving TODO(LP-7450) is a pre-requisite.
 *
 * Currently, ScratchpadUI.init() determines the value of showProjectEvalArea based
 * in part on the current user's profile.  Part of ScratchpadUI.init()'s job is to
 * also render ScratchpadView (from scratchpad.jsx).  It's this component that ends
 * up using the showProjectEvalArea.  If ScratchpadPage rendered the ScratchpadView
 * it could pass showProjectEvalArea as a prop directly and avoid calling the .init()
 * function more than is necessary.
 */
const ExistingUserProgramPage = (): React.ReactElement => {
    // @ts-expect-error - TS2339 - Property 'scratchpad_id' does not exist on type '{}'.
    const {scratchpad_id: scratchpadId} = useParams();
    const {profile, status: profileStatus} = useProfile();
    const scratchpadResult = useProgram(scratchpadId);

    const title =
        scratchpadResult.status === "success"
            ? (scratchpadResult.data.scratchpad.translatedTitle ?? "")
            : "";

    useUpdatePageSettings({title, noIndex: true});

    const isAIGuideEnabled = profile?.get("isAIGuideEnabled");
    useEffect(() => {
        if (isAIGuideEnabled) {
            GuideStateApi.set("selectedBotName", "UserProgram");
        }
    }, [isAIGuideEnabled]);

    if (
        profileStatus === "loading" ||
        scratchpadResult.status === "loading" ||
        scratchpadResult.status === "no-data"
    ) {
        return <RouteLoadSpinner />;
    }

    if (
        scratchpadResult.status === "error" &&
        scratchpadResult.error.name === "KANotFoundError"
    ) {
        return <NotFound />;
    }

    if (
        profileStatus === "error" ||
        profileStatus === "aborted" ||
        scratchpadResult.status === "error" ||
        scratchpadResult.status === "aborted"
    ) {
        return <ErrorLoadingPage />;
    }

    return (
        <PageRoot template={DefaultWithKhanmigoPageTemplate}>
            <ScratchpadPage
                options={scratchpadResult.data}
                profile={profile}
                style={styles.bodyWrapper}
            />
        </PageRoot>
    );
};

const styles = StyleSheet.create({
    bodyWrapper: {
        marginLeft: 16,
        marginRight: 16,
    },
});

export default withStrings()(ExistingUserProgramPage);
